/**
 * Browser binding for personal energy model fetches, Worker messages and checkpoint uploads.
 * Rust owns minibatch training, evidence refresh cadence and checkpoint selection. Background
 * runs retain their Worker and optimizer while the compute scheduler permits work, pausing on
 * backoff and cancellation. Manual runs finish after their initial checkpoint.
 * @module apps/web/src/lib/energyModel
 */
import { request } from './api';
import {
  computeScheduler,
  type ComputeBackoffReason,
  type ComputeJobContext,
  type ComputeJobHandle,
  type ComputeScheduler,
} from './compute';

/** Retrain when the newest model is older than this, even without new data (24 hours). */
export const RETRAIN_AFTER_MS = 24 * 60 * 60 * 1000;
/** Seed for deterministic training; the same data then yields the same model. */
export const TRAINING_SEED = 1;

/** Holdout metrics stored with each model (see `crates/energy-model/src/contract.rs`). */
export interface EnergyModelMetrics {
  /** Exact trainable parameter count per member. */
  parametersPerMember?: number;
  /** Exact trainable parameter count across the ensemble. */
  totalParameters?: number;
  /** Samples before the holdout. */
  trainSamples: number;
  /** Held-out self-reported check-ins. */
  testSamples: number;
  /** Check-ins available. */
  checkIns: number;
  /** Flux Agent observations used (with consent). */
  observations: number;
  /** Profile MAE per axis `[productivity, rejuvenation, resistance]`; null without holdout values. */
  baselineMae: (number | null)[];
  /** Ensemble MAE per axis. */
  ensembleMae: (number | null)[];
  /** Relative MAE improvement per axis. */
  improvement: (number | null)[];
  /** Relative improvement of the summed MAE. */
  meanImprovement: number | null;
  /** Whether the promotion gate passed. */
  promoted: boolean;
  /** Why the gate passed or failed. */
  reason: string;
  /** Worker training time in milliseconds. */
  trainingMs?: number | null;
}

/** One stored model as summarized by `GET /api/energy-model`. */
export interface EnergyModelSummary {
  /** Model id. */
  id: string;
  /** Holdout metrics, or null when unreadable. */
  metrics: EnergyModelMetrics | null;
  /** Samples the model trained on. */
  sampleCount: number;
  /** Whether it passed the promotion gate. */
  promoted: boolean;
  /** Training cutoff (RFC 3339). */
  trainedAt: string;
  /** Upload time. */
  createdAt: string;
}

/** Forecast summary for the UI. */
export interface EnergyForecastSummary {
  /** Model that produced it. */
  modelId: string;
  /** Upload time. */
  createdAt: string;
  /** Whether the planner still uses it (under 36 hours old). */
  fresh: boolean;
  /** Mean axis standard deviation over the first 72 hours. */
  meanUncertainty: number;
  /** Number of hourly points. */
  hours: number;
}

/** One weekly energy estimate; Sunday is zero. */
export interface EnergyHeatmapCell {
  /** Local weekday, Sunday=0. */
  weekday: number;
  /** Local hour, 0–23. */
  hour: number;
  /** Productivity, 0–10. */
  productivity: number;
  /** Rejuvenation, 0–10. */
  rejuvenation: number;
  /** Resistance, 0–10. */
  resistance: number;
  /** Productivity evidence confidence, 0–1; absent only on older servers. */
  productivityConfidence?: number;
  /** Rejuvenation evidence confidence, 0–1; absent only on older servers. */
  rejuvenationConfidence?: number;
  /** Resistance evidence confidence, 0–1; absent only on older servers. */
  resistanceConfidence?: number;
  /** Evidence confidence, 0–1; zero means unknown. */
  confidence: number;
}

/** Weekly personal energy map. */
export interface EnergyHeatmap {
  /** IANA timezone. */
  timezone: string;
  /** The 168 weekly hour cells. */
  cells: EnergyHeatmapCell[];
}

/** Response of `GET /api/energy-model`. */
export interface EnergyModelStatus {
  /** Weekly map, absent only on older servers. */
  heatmap?: EnergyHeatmap;
  /** Newest model, if any. */
  latest: EnergyModelSummary | null;
  /** Up to three newest models. */
  models: EnergyModelSummary[];
  /** Current forecast summary, if any. */
  forecast: EnergyForecastSummary | null;
  /** Newest time any training input changed, if known. */
  latestDataAt: string | null;
}

/** What the Worker returns from a finished run. */
export interface EnergyRunResult {
  /** Body for `PUT /api/energy-model`. */
  upload: {
    /** Correction generation carried unchanged from the Rust training payload. */
    evidenceEpoch?: string;
    /** Architecture identifier. */
    architecture: string;
    /** Layer widths and input columns. */
    featureSchema: unknown;
    /** Base64 model bytes. */
    weights: string;
    /** Holdout metrics. */
    metrics: EnergyModelMetrics;
    /** Training samples. */
    sampleCount: number;
    /** Gate result. */
    promoted: boolean;
  };
  /** Hourly forecast points; empty unless promoted. */
  forecast: unknown[];
  /** Serialized model size. */
  modelBytes: number;
}

/** Messages the page sends to the energy Worker. */
export type EnergyWorkerRequest =
  | {
      /** Browser fetch binding delivers updated labels at Rust's requested cadence. */
      type: 'data';
      /** Latest authenticated training payload. */
      trainingJson: string;
    }
  | {
      /** Start a run. */
      type: 'train';
      /** Correlation id. */
      id: string;
      /** Training payload JSON. */
      trainingJson: string;
      /** Deterministic seed. */
      seed: number;
      /** Keep the cooperative background job alive after its first checkpoint. */
      continuous?: boolean;
    }
  | {
      /** Pause, resume or cancel the current run. */
      type: 'pause' | 'resume' | 'cancel';
    };

/** Messages the energy Worker sends back. */
export type EnergyWorkerReply =
  | {
      /** Rust requests fresh training evidence. */
      type: 'refresh';
      /** Run correlation id. */
      id: string;
    }
  | {
      /** One more ensemble member trained. */
      type: 'progress';
      /** Correlation id. */
      id: string;
      /** Batches trained. */
      step: number;
      /** Initial batch upper bound. */
      total: number;
    }
  | {
      /** The run finished. */
      type: 'result' | 'checkpoint';
      /** Correlation id. */
      id: string;
      /** Upload body and forecast. */
      result: EnergyRunResult;
    }
  | {
      /** The run failed or was cancelled. */
      type: 'error';
      /** Correlation id. */
      id: string;
      /** Safe diagnostic. */
      error: string;
    };

/** Outcome of one runner invocation. */
export type EnergyRunOutcome =
  | {
      /** Nothing to do (fresh model, unsupported device, or data saver). */
      status: 'skipped';
      /** Why. */
      reason: string;
    }
  | {
      /** A model was trained and uploaded. */
      status: 'trained';
      /** Whether it was promoted (and its forecast uploaded). */
      promoted: boolean;
      /** Holdout metrics. */
      metrics: EnergyModelMetrics;
    };

/** API calls the runner needs; injectable for tests. */
export interface EnergyModelApi {
  /** Read status. */
  status(): Promise<EnergyModelStatus | null>;
  /** Read the training payload as JSON text. */
  trainingData(): Promise<string>;
  /** Store a model; returns its id. */
  uploadModel(upload: EnergyRunResult['upload']): Promise<string>;
  /** Store the forecast of a promoted model. */
  uploadForecast(modelId: string, points: unknown[]): Promise<void>;
}

/** Throw a readable error for an unsuccessful API envelope. */
function unwrap<T>(response: {
  /** Whether the call succeeded. */
  success: boolean;
  /** Payload. */
  data?: T;
  /** Failure detail. */
  error?: {
    /** Failure message. */
    message: string;
  };
}): T {
  if (!response.success || response.data === undefined)
    throw new Error(response.error?.message || 'Energy model request failed.');
  return response.data;
}

/** Default API bindings over the credentialed request helper. */
export const energyModelApi: EnergyModelApi = {
  async status() {
    const response = await request<EnergyModelStatus>('/energy-model');
    return response.success ? (response.data ?? null) : null;
  },
  async trainingData() {
    return JSON.stringify(unwrap(await request<unknown>('/energy-model/training-data')));
  },
  async uploadModel(upload) {
    const ownerId = shared?.userId;
    const response = await request<{ id: string }>('/energy-model', {
      method: 'PUT', body: JSON.stringify(upload),
    });
    // Binding: a server evidence fence requests the same owner-scoped invalidation as realtime.
    // Capturing the owner before fetch prevents a late old-account response targeting a new one.
    if (!response.success && response.error?.code === 'ENERGY_EVIDENCE_CHANGED' && ownerId) {
      window.dispatchEvent(new CustomEvent('fluxability-energy-evidence-changed', { detail: { ownerId } }));
    }
    return unwrap(response).id;
  },

  async uploadForecast(modelId, points) {
    unwrap(
      await request<unknown>('/energy-model/forecast', {
        method: 'PUT',
        body: JSON.stringify({ modelId, points }),
      })
    );
  },
};

/** True when there is no model, inputs changed after the last training cutoff, or it is over 24 hours old. */
export function shouldRetrain(status: EnergyModelStatus | null, now = Date.now()): boolean {
  const latest = status?.latest;
  if (!latest) return true;
  const trainedAt = Date.parse(latest.trainedAt);
  if (!Number.isFinite(trainedAt)) return true;
  if (now - trainedAt >= RETRAIN_AFTER_MS) return true;
  const dataAt = status?.latestDataAt ? Date.parse(status.latestDataAt) : NaN;
  return Number.isFinite(dataAt) && dataAt > trainedAt;
}

/** Navigator fields the capability check reads. */
export interface CapabilityNavigator {
  /** Logical cores, when exposed. */
  hardwareConcurrency?: number;
  /** Approximate device memory in GiB, when exposed. */
  deviceMemory?: number;
  /** Network information, when exposed. */
  connection?: {
    /** Data-saver preference. */
    saveData?: boolean;
  };
}

/**
 * Automatic training runs only with Worker and WebAssembly support, at least 4 cores and 2 GiB
 * (when the browser reports them), and without the data-saver preference.
 */
export function isCapableDevice(
  nav: CapabilityNavigator | undefined = typeof navigator === 'undefined'
    ? undefined
    : (navigator as CapabilityNavigator)
): boolean {
  if (!nav || typeof Worker === 'undefined' || typeof WebAssembly === 'undefined') return false;
  if (nav.connection?.saveData) return false;
  if (typeof nav.hardwareConcurrency === 'number' && nav.hardwareConcurrency < 4) return false;
  if (typeof nav.deviceMemory === 'number' && nav.deviceMemory < 2) return false;
  return true;
}

/** Create the dedicated energy Worker (module Worker bundled by Vite). */
export function createEnergyWorker(): Worker {
  return new Worker(new URL('../workers/energy.worker.ts', import.meta.url), { type: 'module' });
}

/** Minimal document surface used to pause while hidden; injectable for tests. */
export interface VisibilitySource {
  /** Current visibility. */
  visibilityState: string;
  /** Subscribe to visibility changes. */
  addEventListener(type: 'visibilitychange', listener: () => void): void;
  /** Unsubscribe. */
  removeEventListener(type: 'visibilitychange', listener: () => void): void;
}

/**
 * Browser binding that runs one training Worker at a time and uploads Rust-selected checkpoints.
 * Background jobs retain their optimizer until cancelled; manual runs release it on completion.
 */
export class EnergyTrainingRunner {
  /** Current run, so overlapping callers share it instead of starting another. */
  private active?: Promise<EnergyRunOutcome>;
  /** Scheduler job of the current run. */
  private job?: ComputeJobHandle<EnergyRunOutcome>;
  /** Set by a forced `run` call; consumed when the job starts. */
  private forceNext = false;
  /** Worker of the current run. */
  private worker?: Worker;
  /** Rejects the current Worker wait on cancellation. */
  private abort?: (error: Error) => void;
  /** Set by `cancel()` so a run stopped during a network call never trains or uploads. */
  private cancelled = false;
  /** Whether the running Worker is a persistent background session. */
  private continuousActive = false;
  /** Cancellation generation prevents a manual run from restarting after sign-out. */
  private cancellationGeneration = 0;

  /** Inject the Worker factory, API and visibility source for testability. */
  constructor(
    /** Creates the dedicated Worker. */
    private readonly factory: () => Worker = createEnergyWorker,
    /** API bindings. */
    private readonly api: EnergyModelApi = energyModelApi,
    /** Visibility source; defaults to `document` when present. */
    private readonly visibility: VisibilitySource | undefined = typeof document === 'undefined'
      ? undefined
      : document,
    /** Progress callback for the Settings UI. */
    public onProgress?: (step: number, total: number) => void,
    /** Compute scheduler the runs are queued on; defaults to the page-wide instance. */
    private readonly scheduler: ComputeScheduler = computeScheduler,
    /** Browser capability probe; injectable without changing the Worker protocol. */
    private readonly capable: () => boolean = isCapableDevice
  ) {}

  /** Whether a run is in progress. */
  get running(): boolean {
    return Boolean(this.active);
  }

  /**
   * Start (or join) a run. `force` skips the freshness and device checks (manual retrain) and
   * runs on the `visible` lane; otherwise the run is a `background` job, which the scheduler
   * starts only after its start-up quiet period and at idle. A forced call while an automatic
   * run is still waiting upgrades it: the scheduler promotes the joined job to `visible`, and it
   * runs forced. Any other overlapping call shares the current run.
   */
  run(
    options: {
      /** Retrain even when the model is fresh or the device is low-capability. */
      force?: boolean;
    } = {}
  ): Promise<EnergyRunOutcome> {
    const force = Boolean(options.force);
    if (force && this.continuousActive && this.active) {
      const previous = this.active;
      this.cancel();
      const generation = this.cancellationGeneration;
      return previous
        .catch(() => undefined)
        .then(() => {
          if (generation !== this.cancellationGeneration)
            throw new Error('Energy model training cancelled.');
          return this.run({ force: true });
        })
        .finally(() => {
          if (generation === this.cancellationGeneration) this.run().catch(() => undefined);
        });
    }
    // Read when the job starts, so a forced join of a still-queued automatic run takes effect.
    if (force) this.forceNext = true;
    if (this.job && this.active && !(force && this.job.state === 'queued')) return this.active;
    const job = this.scheduler.schedule<EnergyRunOutcome>({
      name: 'energy-training',
      lane: force ? 'visible' : 'background',
      key: 'energy-training',
      dedupe: 'join',
      run: context => {
        const forced = this.forceNext;
        this.forceNext = false;
        return this.execute(forced, context);
      },
    });
    if (job !== this.job) {
      this.job = job;
      this.active = job.promise.finally(() => {
        if (this.job === job) {
          this.job = undefined;
          this.active = undefined;
          this.continuousActive = false;
        }
      });
    }
    return this.active!;
  }

  /** Binding: discard stale evidence immediately, then restart after the old job releases its slot.
   * A later cancellation (including account switch) prevents the restart. */
  async invalidateEvidence(): Promise<void> {
    const previous = this.active;
    this.cancel();
    const generation = this.cancellationGeneration;
    await previous?.catch(() => undefined);
    if (generation === this.cancellationGeneration) void this.run().catch(() => undefined);
  }

  /** Cancel a run that has not started yet (leaves a running one alone). */
  cancelPending(): void {
    if (this.job?.state === 'queued') this.job.cancel();
  }

  /** Cancel the current run and terminate its Worker. */
  cancel(): void {
    this.cancelled = true;
    this.cancellationGeneration += 1;
    this.forceNext = false;
    this.job?.cancel();
    this.abort?.(new Error('Energy model training cancelled.'));
    this.worker?.terminate();
    this.worker = undefined;
  }

  /** One full run. */
  private async execute(force: boolean, context?: ComputeJobContext): Promise<EnergyRunOutcome> {
    this.cancelled = false;
    this.continuousActive = !force;
    const ensureActive = () => {
      if (this.cancelled || context?.signal.aborted)
        throw new Error('Energy model training cancelled.');
    };
    if (!force && !this.capable()) return { status: 'skipped', reason: 'device' };
    const trainingJson = await this.api.trainingData();
    ensureActive();
    const upload = async (result: EnergyRunResult) => {
      ensureActive();
      const modelId = await this.api.uploadModel(result.upload);
      ensureActive();
      if (result.upload.promoted && result.forecast.length > 0)
        await this.api.uploadForecast(modelId, result.forecast);
    };
    const result = await this.train(trainingJson, context, !force, upload);
    await upload(result);
    return { status: 'trained', promoted: result.upload.promoted, metrics: result.upload.metrics };
  }

  /** Bind Worker lifetime, scheduler backoff, refreshed evidence and serialized checkpoint uploads. */
  private train(
    trainingJson: string,
    context?: ComputeJobContext,
    continuous = false,
    checkpoint?: (result: EnergyRunResult) => Promise<void>
  ): Promise<EnergyRunResult> {
    const worker = this.factory();
    this.worker = worker;
    const id = `energy-${Date.now().toString(36)}`;
    let paused = false;
    let uploads = Promise.resolve();
    const update = () => {
      const shouldPause =
        this.visibility?.visibilityState === 'hidden' || Boolean(context?.backoff());
      if (shouldPause === paused) return;
      paused = shouldPause;
      worker.postMessage({ type: paused ? 'pause' : 'resume' } satisfies EnergyWorkerRequest);
    };
    const onVisibility = () => update();
    let unsubscribe: (() => void) | undefined;
    return new Promise<EnergyRunResult>((resolve, reject) => {
      this.abort = reject;
      context?.signal.addEventListener('abort', () => reject(context.signal.reason), {
        once: true,
      });
      worker.onmessage = (event: MessageEvent<EnergyWorkerReply>) => {
        const reply = event.data;
        if (!reply || reply.id !== id) return;
        if (reply.type === 'refresh') {
          this.api
            .trainingData()
            .then(trainingJson => {
              worker.postMessage({ type: 'data', trainingJson } satisfies EnergyWorkerRequest);
            })
            .catch(() => undefined);
        } else if (reply.type === 'progress') this.onProgress?.(reply.step, reply.total);
        else if (reply.type === 'checkpoint') {
          uploads = uploads.then(() => checkpoint?.(reply.result)).then(() => undefined);
          uploads.catch(reject);
        } else if (reply.type === 'result') resolve(reply.result);
        else if (reply.type === 'error') reject(new Error(reply.error));
      };
      worker.onerror = event => reject(new Error(event.message || 'Energy worker failed.'));
      this.visibility?.addEventListener('visibilitychange', onVisibility);
      unsubscribe = context?.onBackoffChange((_reason: ComputeBackoffReason) => update());
      worker.postMessage({
        type: 'train',
        id,
        trainingJson,
        seed: TRAINING_SEED,
        continuous,
      } satisfies EnergyWorkerRequest);
      update();
    }).finally(() => {
      this.visibility?.removeEventListener('visibilitychange', onVisibility);
      unsubscribe?.();
      this.abort = undefined;
      worker.terminate();
      if (this.worker === worker) this.worker = undefined;
    });
  }
}

/** Runner shared by the idle hook and Settings, replaced when the signed-in user changes. */
let shared:
  | {
      /** Owner of the runner. */
      userId: string;
      /** The runner. */
      runner: EnergyTrainingRunner;
    }
  | undefined;

/** The shared runner for `userId`; a different user cancels and replaces the previous one. */
export function energyTrainingRunner(userId: string): EnergyTrainingRunner {
  if (shared?.userId !== userId) {
    shared?.runner.cancel();
    shared = { userId, runner: new EnergyTrainingRunner() };
  }
  return shared.runner;
}

/** Cancel and drop the shared runner (sign-out). */
export function resetEnergyTrainingRunner(): void {
  shared?.runner.cancel();
  shared = undefined;
}

/** Load the status for Settings. */
export function fetchEnergyModelStatus(): Promise<EnergyModelStatus | null> {
  return energyModelApi.status();
}
